import { NgModule } from '@angular/core';
import { RouterModule, Routes } from '@angular/router';
import { LanguageGuard } from './shared/guards/language.guard';
import { PAGE_TITLES, PATH_TRANSLATIONS, SupportedLang } from './shared/config/localized-paths';
import { OverviewComponent } from './overview/overview.component';
import { TestimonialsComponent } from './testimonials/testimonials.component';
import { TermsComponent } from './global-footer/terms/terms.component';
import { PrivacyPolicyComponent } from './global-footer/privacy-policy/privacy-policy.component';
import { StudentPrivacyNoticeComponent } from './global-footer/student-privacy-notice/student-privacy-notice.component';
import { AboutUsComponent } from './about-us/about-us.component';
import { Grades23Component } from './grades/grades2-3/grades2-3.component';
import { GradesK1Component } from './grades/gradesk-1/grades-k-1.component';
import { MaterialsComponent } from './materials/materials.component';
import { Grades78Component } from './grades/grades7-8/grades7-8.component';
import { Grades1112Component } from './grades/grades11-12/grades11-12.component';
import { Grades910Component } from './grades/grades9-10/grades9-10.component';
import { Grades45Component } from './grades/grades4-5/grades4-5.component';
import { Grades6Component } from './grades/grades6/grades6.component';
import { PageNotFoundComponent } from './page-not-found/page-not-found.component';
import { WebinarSignUpComponent } from './webinar-sign-up/webinar-sign-up.component';
import { OrderMaterialsComponent } from './order-materials/order-materials.component';
import { UpdatesComponent } from './updates/updates.component';
import { HomeV2Component } from './home-v2/home-v2.component';
import { InvestComponent } from './invest/invest.component';
import { DigitalComponent } from './digital/digital.component';
import { LearningLibraryComponent } from './learning-library/learning-library.component';
import { NicComponent } from './nic/nic.component';

/**
 * Builds the child route list for a single language using the path segments
 */
function buildChildren(p: Record<string, string>, locale: SupportedLang): Routes {
  return [
    { path: '', redirectTo: p['home'], pathMatch: 'full' },
    {
      path: p['home'],
      component: HomeV2Component,
      title: 'My Classroom Economy',
    },
    { path: p['overview'], component: OverviewComponent, title: PAGE_TITLES[locale].overview },
    {
      path: p['testimonials'],
      component: TestimonialsComponent,
      title: 'Testimonials',
    },
    { path: p['updates'], component: UpdatesComponent, title: 'Updates' },
    { path: p['terms'], component: TermsComponent, title: 'Terms' },
    {
      path: p['privacy'],
      component: PrivacyPolicyComponent,
      title: 'Privacy Notice',
    },
    {
      path: p['studentprivacy'],
      component: StudentPrivacyNoticeComponent,
      title: 'Student Privacy Notice',
    },
    { path: p['aboutus'], component: AboutUsComponent, title: 'About Us' },
    {
      path: p['webinar'],
      component: WebinarSignUpComponent,
      title: 'Webinar Sign-Up',
    },
    { path: p['materials'], component: MaterialsComponent, title: 'Materials' },
    {
      path: p['gradesk-1'],
      component: GradesK1Component,
      title: 'Grades K to 1',
    },
    {
      path: p['grades2-3'],
      component: Grades23Component,
      title: 'Grades 2 to 3',
    },
    {
      path: p['grades4-5'],
      component: Grades45Component,
      title: 'Grades 4 to 5',
    },
    { path: p['grade6'], component: Grades6Component, title: 'Grade 6' },
    {
      path: p['grades7-8'],
      component: Grades78Component,
      title: 'Grades 7 to 8',
    },
    {
      path: p['grades9-10'],
      component: Grades910Component,
      title: 'Grades 9 to 10',
    },
    {
      path: p['grades11-12'],
      component: Grades1112Component,
      title: 'Grades 11 to 12',
    },
    {
      path: p['order-materials'],
      component: OrderMaterialsComponent,
      title: 'Order Materials',
    },
    {
      path: p['digital'],
      component: DigitalComponent,
      title: PAGE_TITLES[locale].digital,
    },
    {
      path: p['invest'],
      component: InvestComponent,
      title: PAGE_TITLES[locale].invest,
    },
    {
      path: p['learning-library'],
      component: LearningLibraryComponent,
      title: PAGE_TITLES[locale].learningLibrary,
    },
    {
      path: p['nic'],
      component: NicComponent,
      title: PAGE_TITLES[locale].nic,
    },
    { path: '**', component: PageNotFoundComponent, title: 'Page Not Found' },
  ];
}

const routes: Routes = [
  {
    path: '',
    redirectTo: '/en/home',
    pathMatch: 'full',
  },
  {
    path: 'invest',
    redirectTo: '/en/invest',
    pathMatch: 'full',
  },
  {
    path: 'digital',
    redirectTo: '/en/digital',
    pathMatch: 'full',
  },
  {
    path: 'en',
    canActivate: [LanguageGuard],
    children: buildChildren(PATH_TRANSLATIONS.en, 'en'),
  },
  {
    path: 'es',
    canActivate: [LanguageGuard],
    children: buildChildren(PATH_TRANSLATIONS.es, 'es'),
  },
  {
    path: '**',
    component: PageNotFoundComponent,
    title: 'Page Not Found',
  },
];

@NgModule({
  imports: [
    RouterModule.forRoot(routes, {
      scrollPositionRestoration: 'top',
    }),
  ],
  exports: [RouterModule],
})
export class AppRoutingModule {}
